/**
 * English catalogue. English is the platform default and the fallback locale
 * (PRD NFR-006), so this file is the source of truth for the key set: every other
 * catalogue is typed against it and a missing key is a compile error, not a silent
 * English string in production.
 *
 * Values are either a plain template or a plural record. `{name}` placeholders are
 * interpolated by `t()`; plural records are selected with Intl.PluralRules.
 *
 * The `stage.*` strings under "Guest stage model" are copied verbatim from PRD
 * GST-036 and must not be rewritten without a PRD change - the guest journey suite
 * (TST-001) asserts against them in both locales.
 */

export const en = {
  // -------------------------------------------------------------------------
  // Brand
  // -------------------------------------------------------------------------
  'brand.name': 'Croissant in Bed',
  'brand.tagline': "Stay in bed. We'll bring the croissant.",

  // -------------------------------------------------------------------------
  // Guest stage model - PRD GST-036. Verbatim; do not paraphrase.
  // -------------------------------------------------------------------------
  'stage.orderPlaced.title': 'Order placed',
  'stage.orderConfirmed.title': 'Order confirmed',
  'stage.preparing.title': 'Preparing',
  'stage.preparing.message': 'Your baker is preparing your order',
  'stage.orderReady.title': 'Order ready for pickup',
  'stage.orderReady.message': 'Your order is ready at the bakery',
  'stage.findingCourier.title': 'Finding a courier',
  'stage.findingCourier.message': "We're finding a courier to pick up your order",
  'stage.courierAssigned.title': 'Courier assigned',
  'stage.courierAssigned.message': '{name} is on the way to the bakery',
  'stage.onTheWay.title': 'On the way',
  'stage.onTheWay.message': '{name} has picked up your order and is on the way',
  'stage.arriving.title': 'Arriving',
  'stage.arriving.message': '{name} is arriving now - get ready to pick up your order',
  'stage.delivered.title': 'Delivered',
  'stage.delivered.message': 'Your order has been delivered',

  // -------------------------------------------------------------------------
  // Pill words (#2004). The word a status pill shows, once for every portal:
  // `pill.<family>.<state>`, the family and its colours being the list in
  // `@croissants/ui` (status-pill.tsx). A screen never writes these itself.
  // -------------------------------------------------------------------------
  'pill.payout.PENDING': 'Pending',
  'pill.payout.PAID': 'Paid',
  'pill.payout.FAILED': 'Failed',
  'pill.payout.CANCELED': 'Canceled',
  // The order on Internal Ops. Internal is English-only.
  'pill.orderSummary.PAYMENT_PENDING': 'Payment pending',
  'pill.orderSummary.AWAITING_BAKERY': 'Awaiting bakery',
  'pill.orderSummary.PREPARING': 'Preparing',
  'pill.orderSummary.READY': 'Ready',
  'pill.orderSummary.ON_THE_WAY': 'In transit',
  'pill.orderSummary.DELIVERED': 'Delivered',
  'pill.orderSummary.CANCELLED': 'Canceled',
  // Why an order reads as it does (#2004, approved 2026-10-08, section 3):
  // the cause the order records, beside Canceled, and a courier search still
  // running, beside Ready.
  'pill.orderSummary.reason.CASCADE_EXHAUSTED': 'No bakery accepted',
  'pill.orderSummary.reason.NO_ELIGIBLE_HOUSE': 'No bakery serves the apartment',
  'pill.orderSummary.reason.WINDOW_SWEEP': 'Nobody answered',
  'pill.orderSummary.reason.COUNTER_GAVE_BACK': 'Bakery gave it back',
  'pill.orderSummary.reason.NEVER_PREPARED': 'Never prepared',
  'pill.orderSummary.reason.CLOSURE': 'Bakery closed',
  'pill.orderSummary.reason.GUEST': 'Canceled by the guest',
  'pill.orderSummary.reason.OPERATOR': 'Canceled by an operator',
  'pill.orderSummary.reason.NO_COURIER': 'No courier found',
  'pill.orderSummary.reason.NEVER_COLLECTED': 'Never collected',
  'pill.orderSummary.reason.DELIVERY_FAILED': 'Delivery failed',
  'pill.orderSummary.reason.PROPERTY_REMOVED': 'Apartment removed',
  'pill.orderSummary.reason.NEVER_PAID': 'Card refused',
  'pill.orderSummary.reason.SEARCHING_UNTIL': 'No courier yet, searching until {time}',
  'pill.orderSummary.reason.NO_COURIER_YET': 'No courier yet',
  // The Orders list's merged states keep their old word beside the new one.
  'pill.orderLifecycle.reason.SCHEDULED': 'Scheduled',
  'pill.orderLifecycle.reason.OPEN_TO_ANSWER': 'Open to answer',
  'pill.orderLifecycle.reason.RINGING': 'Ringing at the counter',
  'pill.orderLifecycle.reason.CLOSED': 'Closed',
  'pill.courierJob.CREATED': 'Searching',
  'pill.courierJob.COURIER_ASSIGNED': 'Assigned',
  'pill.courierJob.PICKED_UP': 'In transit',
  'pill.courierJob.ARRIVING': 'Arriving',
  'pill.courierJob.RETURNING': 'Returning',
  'pill.courierJob.DELIVERED': 'Delivered',
  'pill.courierJob.CANCELLED': 'Canceled',
  'pill.courierJob.FAILED': 'Failed',
  'pill.latenessBand.LATE': 'Late',
  'pill.latenessBand.AT_RISK': 'At risk',
  'pill.latenessBand.WATCH': 'Watch',
  'pill.latenessBand.ON_TRACK': 'On track',
  'pill.timingVerdict.ON_TIME': 'OK',
  'pill.timingVerdict.EARLY': 'Early',
  'pill.timingVerdict.LATE': 'Late',
  'pill.timingVerdict.MISSED': 'Missed',
  'pill.timingVerdict.PENDING': 'Pending',
  'pill.tag.ORDERING_CLOSED': 'Ordering closed',
  'pill.tag.NOW': 'Now',
  'pill.tag.ROBOBAKER': 'Robobaker',
  'pill.tag.SERVES_NOWHERE': 'Serves nowhere',
  'pill.tag.SIMULATION': 'Simulation',
  'pill.tag.DEV_SESSION': 'Dev session',
  'pill.tag.MULTI_DAY_STAY': 'Stay',
  // The counters and the service on Internal Ops. Internal is English-only.
  'pill.bakeryService.OPEN': 'Open',
  'pill.bakeryService.UNREACHABLE': 'Offline',
  'pill.bakeryService.CLOSED': 'Closed',
  'pill.bakeryService.PAUSED': 'Paused',
  'pill.bakeryService.INCOMPLETE': 'Not ready',
  'pill.bakeryService.SUSPENDED': 'Suspended',
  'pill.bakeryService.reason.NO_DEVICE_ANSWERING': 'No device answering',
  'pill.bakeryService.reason.NO_DEVICE_PAIRED': 'No device paired',
  'pill.bakeryService.reason.CLOSURE_THROUGH': 'Closure through {date}',
  'pill.bakeryService.reason.BAKER': 'By the baker',
  'pill.bakeryService.reason.KILL_SWITCH': 'Kill switch on',
  'pill.bakeryService.reason.TWO_UNANSWERED': 'Two orders unanswered',
  'pill.bakeryService.reason.PICKUP_DETAILS': 'Pickup details missing',
  'pill.bakeryService.reason.PAYOUT_SETUP': 'Payout setup unfinished',
  'pill.bakeryService.reason.VAT': 'VAT not stated',
  'pill.deviceConnectivity.READY': 'Online',
  'pill.deviceConnectivity.UNREACHABLE': 'Offline',
  'pill.deviceConnectivity.reason.IN_BACKGROUND': 'In background',
  'pill.device.ONLINE': 'Online',
  'pill.device.UPDATING': 'Updating',
  'pill.device.OFFLINE': 'Offline',
  'pill.device.BLOCKED': 'Blocked',
  'pill.device.reason.IN_BACKGROUND': 'In background',
  'pill.device.reason.CHARGING': 'Last seen charging',
  'pill.device.reason.UPDATE_REQUIRED': 'Update required',
  'pill.standing.ACTIVE': 'Active',
  'pill.standing.SUSPENDED': 'Suspended',
  'pill.standing.INCOMPLETE': 'Not ready',
  'pill.healthVerdict.GREEN': 'OK',
  'pill.healthVerdict.AMBER': 'Warning',
  'pill.healthVerdict.RED': 'Blocked',
  'pill.accountingExport.ON': 'On',
  'pill.accountingExport.OFF': 'Off',
  'pill.alertSeverity.DANGER': 'Serious',
  'pill.alertSeverity.WARNING': 'Warning',
  'pill.alertSeverity.INFO': 'Information',
  'pill.deadLetter.OPEN': 'Open',
  'pill.releaseStep.HALTED': 'Halted',
  'pill.scenarioRun.PASS': 'Pass',
  'pill.scenarioRun.FAIL': 'Fail',
  'pill.scenarioRun.INCONCLUSIVE': 'Inconclusive',
  // The Devices dialog both portals share (Internal and Partner).
  'pill.pairedDevice.ONLINE': 'Online',
  'pill.pairedDevice.UNREACHABLE': 'Offline',
  'pill.pairedDevice.reason.IN_BACKGROUND': 'In background',
  // The money on Internal Ops. Internal is English-only.
  'pill.payableAccount.READY': 'Payable',
  'pill.payableAccount.BLOCKED': 'Blocked',
  'pill.payableAccount.FUNDS_PENDING': 'Pending',
  'pill.payableAccount.reason.NEGATIVE_BALANCE': 'Negative balance',
  'pill.payableAccount.reason.FUNDS_HELD': 'Funds held',
  'pill.payableAccount.reason.BANK_DETAILS': 'Bank details missing',
  'pill.payableAccount.reason.RESTRICTED': 'Restricted by Stripe',
  'pill.payableAccount.reason.IDENTITY': 'Identity check missing',
  'pill.payableAccount.reason.ON_ITS_WAY': 'Payout on its way',
  'pill.payableAccount.reason.FUNDS_NOT_AVAILABLE': 'Funds not available yet',
  'pill.monthlyPayout.READY': 'Payable',
  'pill.monthlyPayout.FUNDS_PENDING': 'Pending',
  'pill.monthlyPayout.NOTHING': 'Nothing',
  'pill.monthlyPayout.PAID': 'Paid',
  'pill.monthlyPayout.FAILED': 'Failed',
  'pill.monthlyPreview.READY': 'Payable',
  'pill.monthlyPreview.FUNDS_PENDING': 'Pending',
  'pill.monthlyPreview.NOTHING': 'Nothing',
  'pill.monthlyPreview.NOT_CLOSED': 'In progress',
  'pill.monthlyPreview.PAID': 'Paid',
  'pill.payoutMovement.PENDING': 'Pending',
  'pill.figure.ESTIMATED': 'Estimated',
  'pill.figure.PENDING': 'Pending',
  'pill.reconciliation.CLEAN': 'No drift',
  'pill.reconciliation.DRIFT': 'Drift',
  'pill.deliveryStatement.OPEN': 'In progress',
  'pill.deliveryStatement.UNMATCHED': 'Unmatched',
  'pill.deliveryStatement.MATCHED': 'Matched',
  'pill.deliveryStatement.DIFFERS': 'Differs',
  'pill.documentExport.PENDING': 'Pending',
  'pill.documentExport.EXPORTED': 'Exported',
  'pill.documentExport.FAILED': 'Failed',
  'pill.payoutAccount.NOT_STARTED': 'Incomplete',
  'pill.payoutAccount.PENDING': 'Pending',
  'pill.payoutAccount.VERIFIED': 'Verified',
  'pill.payoutAccount.RESTRICTED': 'Restricted',
  // The money on both portals: a document's state (Internal's Documents, and
  // Partner's links to a canceled invoice), a partner's payout account on its
  // own Settings, and a commission not in the balance yet on Partner's Orders.
  'pill.document.ISSUED': 'Issued',
  'pill.document.CANCELLED': 'Canceled',
  'pill.payoutOnboarding.INCOMPLETE': 'Incomplete',
  'pill.payoutOnboarding.PENDING': 'Pending',
  'pill.payoutOnboarding.VERIFIED': 'Verified',
  'pill.payoutOnboarding.RESTRICTED': 'Restricted',
  'pill.commission.PENDING': 'Pending',
  'pill.commission.PAID': 'Paid',
  // Partner, the guest, Internal's clients and set menus.
  'pill.qrAllowance.REMAINING': {
    one: '{count} left',
    other: '{count} left',
  },
  'pill.qrAllowance.EXHAUSTED': 'Allowance used',
  'pill.apartment.ARCHIVED': 'Archived',
  'pill.apartment.NOT_SERVABLE': 'Not ready',
  'pill.apartment.PAUSED': 'Paused',
  'pill.apartment.LIVE': 'Active',
  'pill.apartment.reason.NO_BAKERY': 'No bakery reaches it yet',
  'pill.apartment.reason.NO_QR': 'No QR code yet',
  // Internal's client record: the apartment's own words, its reasons worded
  // for an operator. Internal is English-only.
  'pill.clientApartment.reason.NO_BAKERY': 'No bakery reaches it',
  'pill.clientApartment.reason.NO_QR': 'No QR code yet',
  'pill.bulkResult.DONE': 'Done',
  'pill.bulkResult.SKIPPED': 'Skipped',
  'pill.bulkResult.FAILED': 'Failed',
  'pill.bulkResult.reason.ALREADY_ARCHIVED': 'Already archived',
  'pill.bulkResult.reason.WAS_NOT_ACTIVE': 'Was not active',
  'pill.bulkResult.reason.WAS_NOT_PAUSED': 'Was not paused',
  'pill.bulkResult.reason.ARCHIVED': 'Archived',
  'pill.bulkResult.reason.ARCHIVE_FAILED': 'Could not be archived',
  'pill.bulkResult.reason.STILL_TAKING_ORDERS': 'Still taking orders',
  'pill.bulkResult.reason.STILL_PAUSED': 'Still paused',
  'pill.bulkResult.reason.PRINT_FAILED': 'Could not be printed',
  'pill.bulkResult.reason.ALLOWANCE_USED': 'Allowance used up',
  'pill.shop.ACTIVE': 'Active',
  'pill.shop.SUSPENDED': 'Suspended',
  'pill.shop.PAUSED': 'Paused',
  'pill.product.ACTIVE': 'Active',
  'pill.product.RETIRED': 'Retired',
  'pill.supportReason.REMOVED': 'Removed',
  'pill.supportReason.LOST': 'Lost',
  'pill.supportReason.CANCELLED': 'Canceled',
  'pill.supportReason.COMPLAINT': 'Complaint',
  'pill.supportReason.DISPUTE': 'Dispute',
  'pill.supportReason.FRAUD': 'Fraud',
  'pill.supportReason.STRIPE': 'Stripe',
  'pill.supportReason.OTHER': 'Other',
  'pill.supportReason.WASTED_RIDER': 'Rider',
  'pill.supportReason.REFUND_REFUSED': 'Refused',
  'pill.paymentMovement.PENDING': 'Pending',
  'pill.paymentMovement.SENT': 'Sent',
  'pill.paymentMovement.CONFIRMED': 'Done',
  'pill.paymentMovement.FAILED': 'Retrying',
  'pill.paymentMovement.NEEDS_HUMAN': 'Failed',
  'pill.availability.SOLD_OUT_TODAY': 'Sold out',
  'pill.availability.reason.UNTIL_FURTHER_NOTICE': 'Until further notice',
  'pill.teamMember.INVITED': 'Invited',
  'pill.teamMember.ACTIVE': 'Active',
  'pill.teamMember.DISABLED': 'Disabled',
  'pill.formuleHouse.UNPRICED': { one: '{count} unpriced', other: '{count} unpriced' },
  'pill.formuleHouse.NEGATIVE': 'Negative',
  'pill.formuleHouse.reason.EVERY_PICK': 'On every pick',
  'pill.formuleHouse.reason.SOME_PICKS': 'On some picks',
  // The guest's orders list. NOT the tracker's stage titles (`stage.*`, the
  // PRD's own words, GST-036), which stay verbatim on the order page.
  'pill.guestStage.ORDER_PLACED': 'Placed',
  'pill.guestStage.ORDER_CONFIRMED': 'Confirmed',
  'pill.guestStage.PREPARING': 'Preparing',
  'pill.guestStage.ORDER_READY': 'Ready',
  'pill.guestStage.ON_THE_WAY': 'In transit',
  'pill.guestStage.DELIVERED': 'Delivered',
  'pill.guestException.CANCELLED': 'Canceled',
  // Why, as the guest's own message beside Canceled until they dismiss it
  // (#2004, approved 2026-10-08, section 3). None when the guest cancelled.
  'pill.guestException.reason.NO_BAKERY':
    'No bakery could take your order. You will be refunded in full.',
  'pill.guestException.reason.BAKERY_COULD_NOT_PREPARE':
    'Your bakery could not prepare your order. You will be refunded in full.',
  'pill.guestException.reason.BAKERY_CLOSED':
    'Your bakery is closed that day. You will be refunded in full.',
  'pill.guestException.reason.OPERATOR':
    'Our team canceled your order. You will be refunded in full.',
  'pill.guestException.reason.NO_COURIER':
    'No courier could be found for your order. You will be refunded in full.',
  'pill.guestException.reason.NEVER_COLLECTED':
    'No courier came to collect your order. You will be refunded in full.',
  'pill.guestException.reason.COURIER_COULD_NOT_DELIVER':
    'The courier could not deliver your order. You will be refunded in full.',
  'pill.guestException.reason.DELIVERY_RETURNING':
    'Delivery failed, your order is going back to the bakery.',
  'pill.guestException.reason.APARTMENT_REMOVED':
    'This apartment no longer takes orders. You will be refunded in full.',
  'pill.guestException.reason.CARD_REFUSED': 'Your card was refused. Nothing was charged.',
  'pill.guestOrderDue.TODAY': 'Today',
  // What today says once a bakery has taken it: the morning is no longer a
  // plan, it is work in hand. Not a stage - the stages have their own words.
  'pill.guestOrderDue.IN_PROGRESS': 'In progress',
  'pill.guestOrderDue.TOMORROW': 'Tomorrow',
  'pill.guestOrderDue.IN_DAYS': {
    one: 'In {count} day',
    other: 'In {count} days',
  },
  'pill.stockOut.ORDER_ITEMS': {
    one: 'Sold out · {count} item',
    other: 'Sold out · {count} items',
  },
  /* What happened to the BREAD. It said "Refunded", which is a claim about
     money the team had not confirmed yet - and the money is stated once, on
     the order page, where the amount is (founder decision, 2026-09-15). */
  'pill.stockOut.LINE_REMOVED': 'Sold out · Removed',
  'pill.stockOut.UNITS_REMOVED': 'Sold out · {count} removed',
  'pill.stockOut.REPLACED': 'Sold out · Replaced',
  // The Sunmi handheld's History outcome (counterOutcome): Refused and its
  // fallback - En route, Livree and Annulee are the order's own words
  // (PILL_WORD_KEYS) - and the line under Canceled and Refused. The handheld
  // is French-only and draws its own copy of the French (strings.xml,
  // check-counter-pills); these English words say what each means.
  'pill.counterOutcome.REFUSED': 'Refused',
  'pill.counterOutcome.UNKNOWN': 'Finished',
  'pill.counterOutcome.reason.NO_BAKERY': 'No bakery available',
  'pill.counterOutcome.reason.GIVEN_BACK': 'Given back by you',
  'pill.counterOutcome.reason.NEVER_PREPARED': 'Not prepared in time',
  'pill.counterOutcome.reason.CLOSURE': 'Closure',
  'pill.counterOutcome.reason.GUEST': 'Canceled by the guest',
  'pill.counterOutcome.reason.OPERATOR': 'Canceled by our team',
  'pill.counterOutcome.reason.REASSIGNED': 'Given to another bakery',
  'pill.counterOutcome.reason.NO_COURIER': 'No courier found',
  'pill.counterOutcome.reason.NEVER_COLLECTED': 'Never collected',
  'pill.counterOutcome.reason.DELIVERY_FAILED': 'Delivery failed',
  'pill.counterOutcome.reason.PROPERTY_REMOVED': 'Apartment removed',
  'pill.counterOutcome.reason.BY_YOU': 'By you',
  'pill.counterOutcome.reason.NO_ANSWER': 'No answer in time',

  // -------------------------------------------------------------------------
  // Order tracking (PRD GST-008, GST-035, RTE-009)
  // -------------------------------------------------------------------------
  'order.promisedWindow': 'Order for {day}, {window}',
  'order.deliveredOn': 'Delivered {day} at {time}',
  'order.orderCode': 'Order {code}',
  'order.courierVehicle': '{colour} {make} {model}',
  /**
   * Stands in for {name} wherever the courier is not named yet: the state
   * machine allows PICKED_UP and ARRIVING straight from DISPATCHED, so a
   * provider that never sends a courier-assigned event still produces those
   * guest messages (PRD GST-035, GST-036, NTF-001).
   */
  'order.courierUnnamed': 'Your courier',
  'order.contactCourier': 'Contact the courier',
  'order.contactCourierPin': 'PIN {pin}',
  'order.proofOfDelivery': 'Delivery photo',
  'order.downloadReceipt': 'Download receipt',
  'order.itemCount': {
    one: '{count} item',
    other: '{count} items',
  },
  'order.bagCount': {
    one: '{count} bag',
    other: '{count} bags',
  },

  // -------------------------------------------------------------------------
  // The guest's receipt PDF (PRD PAY-022)
  //
  // A receipt, not an invoice (founder decision 2026-08-25): it tells a guest
  // what they ordered, what it cost and that it arrived. The legal apparatus of
  // a facture - per-line VAT, totals both sides of tax, the seller's identity -
  // belongs on a facture, which is a different document with a different job.
  // -------------------------------------------------------------------------
  'receipt.title': 'Receipt',
  'receipt.thanks': 'Thanks for your order, {name}',
  'receipt.forShop': 'Here is your receipt for {shop}.',
  /** Mode A names no house (GST-029), so the sentence loses its object. */
  'receipt.forYou': 'Here is your receipt.',
  'receipt.order': 'Order {code}',
  'receipt.total': 'Total',
  'receipt.itemsSubtotal': 'Items subtotal',
  'receipt.deliveryFee': 'Delivery fee',
  'receipt.serviceFee': 'Service fee',
  'receipt.smallOrderFee': 'Small order fee',
  'receipt.promotion': 'Promotion',
  'receipt.refunded': 'Refunded',
  'receipt.payments': 'Payments',
  'receipt.cardPayment': 'Card payment',
  /** Stated instead of a payment while the card is only authorized. */
  'receipt.toPay': 'To pay',
  'receipt.orderDetails': 'Order details',
  'receipt.pickedUp': 'Picked up',
  'receipt.delivered': 'Delivered',
  'receipt.deliveredBy': 'Delivered by {name}',
  'receipt.orderCompleted': 'Order completed',

  // -------------------------------------------------------------------------
  // Orders screen (PRD GST-034)
  // -------------------------------------------------------------------------
  'orders.title': 'Your orders',
  'orders.active': 'Active',
  'orders.past': 'Past orders',
  'orders.empty': 'No orders yet',
  'orders.reorder': 'Order this again',
  'orders.viewOrder': 'View order',

  // -------------------------------------------------------------------------
  // Storefront (PRD GST-003, GST-029 to GST-032, GST-040)
  // -------------------------------------------------------------------------
  'storefront.title': 'Breakfast, delivered to your door',
  'storefront.chooseShop': 'Choose a bakery',
  'storefront.shopMinimumBasket': 'Minimum order {amount}',
  'storefront.changeShopWarning': 'Changing bakery empties your basket. Do you want to continue?',
  'storefront.notServable.title': 'Not yet available here',
  'storefront.notServable.body':
    'We do not deliver to this address yet. We are adding new neighborhoods every month.',
  'storefront.servicePaused.title': 'Ordering is paused',
  'storefront.servicePaused.body': 'Ordering is paused for now. Please try again a little later.',
  'storefront.soldOutToday': 'Sold out today',
  'storefront.allergens': 'Allergens',
  'storefront.ingredients': 'Ingredients',

  // -------------------------------------------------------------------------
  // Product types (PRD CAT-016) - the two this codebase authored, and the
  // heading for a line whose product the catalogue no longer declares. Every
  // other type is a curator's own words and is its own translation.
  // -------------------------------------------------------------------------
  'productType.MENU': 'Set menus',
  'productType.EXTRA': 'Extras',
  'productType.OTHER': 'Other',

  // -------------------------------------------------------------------------
  // Window picker (PRD GST-002)
  // -------------------------------------------------------------------------
  'windows.chooseDay': 'Choose a day',
  'windows.chooseWindow': 'Choose a 30-minute window',
  'windows.unavailable.capacity': 'Fully booked',
  'windows.unavailable.tooEarly': 'Too early for the bakery',
  'windows.unavailable.closed': 'Bakery closed',

  // -------------------------------------------------------------------------
  // Checkout (PRD GST-006, GST-019 to GST-026, PAY-002)
  // -------------------------------------------------------------------------
  'checkout.title': 'Checkout',
  'checkout.contact': 'Your details',
  'checkout.fullName': 'Full name',
  'checkout.phone': 'Mobile number',
  'checkout.email': 'Email (optional)',
  'checkout.language': 'Language',
  'checkout.deliveryDetails': 'Delivery details',
  'checkout.address': 'Address',
  /*
   * The eight boxes of the apartment's delivery brief, worded ONCE (founder
   * decision 2026-08-24). `prop.field.*` in partner-app is the same list under
   * the same words in the same order - the owner states the brief and the guest
   * corrects it, and two vocabularies for one set of facts is how "Door" came
   * to face "Door number" across two screens of the same product.
   */
  'checkout.building': 'Building',
  'checkout.floor': 'Floor',
  'checkout.doorPosition': 'Door position',
  'checkout.doorNumber': 'Door number',
  'checkout.intercomName': 'Name on the intercom',
  'checkout.accessCode': 'Access code',
  'checkout.lift': 'Lift',
  'checkout.courierInstructions': 'Instructions for the courier',
  /* The FIELD, at both ends of the same fact: the box an owner fills on the
     apartment's delivery brief (`prop.field.accessNotes`) and the box a guest
     fills on their own delivery details are one thing said twice, so they are
     one set of words (founder decision 2026-08-24). The key above is the ROW
     that opens the sheet, which names the subject rather than the field. */
  'checkout.additionalInstructions': 'Additional instructions for the courier',
  'checkout.leaveAtDoor': 'Leave it at the door',
  'checkout.review': 'Review your order',
  'checkout.pay': 'Order and pay · {amount}',
  /* WEB-007: accepted by paying, said where the paying happens. `{terms}` is
     the link itself - the words a guest taps to read what they are accepting -
     so the sentence carries the slot rather than a second sentence carrying a
     second link. The link's own words are `checkout.termsLink`, lower case
     because they sit mid-sentence. */
  /* ...and, since ruling #24 (2026-10-05), the card mandate too: the payment
     sheet no longer draws Stripe's four lines of it inside the card form
     (PaymentSheet.tsx, `terms: { card: 'never' }`), so the authorization to
     charge the saved card later - a stay's next mornings, a tip - is given
     here, in the same sentence, above the button that opens the sheet. */
  'checkout.terms':
    'By paying you accept our {terms} and authorize Croissant in Bed to charge your card for future payments under them.',
  'checkout.termsLink': 'terms and conditions',
  'checkout.minimumBasket': 'This bakery has a minimum order of {amount}.',
  'checkout.minimumBasketShort': 'Add {amount} more to order',

  // -------------------------------------------------------------------------
  // Price breakdown (PRD GST-022)
  // -------------------------------------------------------------------------
  'price.merchandise': 'Items',
  'price.delivery': 'Delivery',
  'price.service': 'Service fee',
  'price.smallOrder': 'Small order fee',
  'price.promotion': 'Promotion',
  'price.tax': 'VAT',
  'price.taxLine': 'VAT {rate}',
  // Every charge row is VAT-inclusive, so VAT is reported as contained in the
  // total rather than added to it - otherwise the rows do not add up on screen
  // (GST-022).
  'price.taxIncluded': 'of which VAT',
  'price.taxIncludedAt': 'of which VAT {rate}',
  'price.total': 'Total',
  // A stay itemises DAY ONE (multi-day plan section 3, D8), so its total row is
  // the per-morning figure every row of the stay reads - never "Total", which
  // beside a stay total on the same screen is two answers to one question.
  'price.totalPerMorning': 'Total per morning',
  'price.free': 'Free',
  // GST-044: what the next step of the delivery grid saves - on the DELIVERY
  // fees only (founder, 2026-09-21) - stated under the address once the basket
  // holds anything, with the free-delivery basket after it when there is one.
  'price.addToSave': 'Add {amount} to save {saving} on your delivery fees.',
  'price.addToSaveFreeAbove':
    'Add {amount} to save {saving} on your delivery fees. Free delivery above {free}.',
  'checkout.freeDeliveryAbove': 'Free delivery above {amount}',

  // -------------------------------------------------------------------------
  // Cancellation (PRD GST-009)
  // -------------------------------------------------------------------------
  'cancel.action': 'Cancel order',
  'cancel.confirmTitle': 'Cancel this order?',
  /* PRD section 4.2 and GST-009: no refund is automatic, so the one sentence
     before the guest presses is what actually happens - the order ends and the
     team confirms the money. It read "Full refund, no fee.", which named a
     movement nobody had authorized yet. */
  'cancel.confirmBody': 'Your full refund is confirmed by our team.',
  'cancel.confirmAction': 'Yes, cancel it',
  'cancel.keepAction': 'Keep my order',

  // -------------------------------------------------------------------------
  // Tip (PRD GST-037, PAY-024)
  // -------------------------------------------------------------------------
  'tip.title': 'Tip your courier?',
  'tip.body': '100% goes to the courier.',
  'tip.custom': 'Other amount',
  'tip.skip': 'No thanks',
  'tip.confirm': 'Tip {amount}',
  'tip.thanks': 'Thank you. Sent to {name}.',

  // -------------------------------------------------------------------------
  // Ratings (PRD GST-038)
  // -------------------------------------------------------------------------
  'rating.title': 'How did it go?',
  'rating.delivery': 'The delivery',
  'rating.products': 'The food',
  'rating.comment': 'Anything to add?',
  'rating.submit': 'Send',
  'rating.skip': 'Skip',
  'rating.thanks': 'Thank you.',
  'rating.stars': {
    one: '{count} star',
    other: '{count} stars',
  },

  // -------------------------------------------------------------------------
  // Issue reporting (PRD GST-039)
  // -------------------------------------------------------------------------
  'issue.title': 'Report a problem',
  'issue.selectItems': 'Which items?',
  'issue.describe': 'What went wrong?',
  'issue.addPhoto': 'Add a photo',
  'issue.submit': 'Send report',
  'issue.submitted': 'Thank you. We are looking into it.',

  // -------------------------------------------------------------------------
  // Notifications (PRD NTF-001, NTF-004)
  // -------------------------------------------------------------------------
  'notification.orderConfirmed':
    'Your breakfast is confirmed for {day}, {window}. Track it: {link}',
  'notification.preparationIssue': 'There is a problem with your order. We are on it: {link}',
  'notification.itemReplaced':
    '{original} sold out, so your order now has {replacement} instead. Details: {link}',
  // BAK-019, founder rule 2 (29 September 2026): a plain counter removal is
  // told to the guest when it happens. The money follows at the close.
  'notification.itemRemoved':
    'The bakery was out of {product} (x{units}). You will not pay for it. Details: {link}',
  // BAK-040. The bakery needs a few more minutes, so the guest is told the new
  // window rather than that "there is a problem": this is the one promise
  // change a guest can act on by staying in bed a little longer.
  'notification.pickupDelayed':
    'Your bakery needs a little more time. Your breakfast now arrives {window}. Details: {link}',
  'notification.onTheWay': '{name} has picked up your breakfast and is on the way. Track: {link}',
  'notification.arriving': '{name} is nearby with your breakfast. Track: {link}',
  // The link is not decoration on these three. SMS goes out from an
  // alphanumeric sender ID that cannot receive a reply (NTF-001, SUP-009), so
  // the order page is the only place a guest can act - and "delivered" is the
  // moment they discover something is missing (GST-039), "cancelled" and
  // "refunded" the moment they want to chase the money (GST-008).
  'notification.delivered': 'Your breakfast has been delivered. Enjoy! Anything wrong? {link}',
  // "canceled and refunded in full" was one sentence about two things, and only
  // the first had happened: the refund is a case an operator decides once the
  // order is final (PAY-030; founder rules of 29 September 2026). The SMS is the one place the money
  // is spoken of at all, because a guest reading it is not on the page that
  // shows it to them.
  'notification.cancelled':
    'Your order was canceled. Your refund is confirmed by our team. Details: {link}',
  // ALC-006 / NTF-003. The second half said "You have not been charged", which
  // stopped being true when capture moved to CHECKOUT (PAY-002, 2026-08-31):
  // every placed order has been charged. The guest-web twin of this notice was
  // corrected then and this one was not. It states the ending and promises no
  // refund: an operator decides the case (founder rule 6, 29 September 2026).
  'notification.orderUnfulfilled':
    'We are sorry - no bakery could take your order right now. It is canceled, and any refund is confirmed by our team. Details: {link}',
  'notification.refundSentence': 'Refund will be processed. Details: {link}',
  'notification.issueResolved': 'We have resolved the problem with your order: {link}',
  'notification.supportReply': 'About your order: {text} Details: {link}',
  'notification.paymentFailed':
    'We could not charge your card for tomorrow morning. Fix it: {link}',
  'notification.deliveryFailed':
    'We could not complete your delivery. We are on it — details and next steps: {link}',
  // RTE-035: once per order, ten minutes into the courier search.
  'notification.courierSearchDelayed':
    'Finding a courier is taking longer than usual. We are still looking: {link}',
  'notification.dataRequestCode':
    'Your Croissant in Bed data request code is {code}. It expires in 15 minutes.',
  // The guest's invoices, both PDFs attached to one email (docs/finance/invoicing.html#delivery).
  'documents.guestInvoicesSubject': 'Your invoices — order {code}',
  'documents.guestInvoicesBody':
    'Your invoices for order {code} are attached to this email as PDF files. They are also on your order page.',

  // -------------------------------------------------------------------------
  // Generic actions and errors
  // -------------------------------------------------------------------------
  'action.continue': 'Continue',
  'action.back': 'Back',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.retry': 'Try again',
  'action.addToBasket': 'Add',
  'action.removeFromBasket': 'Remove',
  'error.network': 'Could not reach the server.',
  'error.unexpected': 'Something went wrong. Please try again.',
  'error.temporarily_unavailable': 'We could not reach our systems. Please try again in a moment.',
  'error.notFound': 'We could not find that.',
  /*
   * The three the HTTP status alone justifies, and the one the delivery partner
   * justifies. They lived in the app catalogues - `error.forbidden` in two of
   * them with different words, `error.conflict` in two more - which is why one
   * 409 read four ways. `errorSentence` names these, so the platform has to
   * carry them; a surface with better words still overrides them by declaring
   * the same key.
   */
  'error.unauthorized': 'Your session is not valid. Sign in again.',
  'error.forbidden': 'You do not have access to that.',
  'error.conflict': 'Something changed while you were working. Refresh and try again.',
  'error.provider_unavailable': 'The delivery provider is not answering.',
  'error.unknown_provider': 'That delivery provider is not configured on this deployment.',
  'error.windowUnavailable': 'That window is no longer available. Please pick another one.',
  'error.capacityExhausted': 'The bakery is fully booked for that window.',
  'error.minimumBasket': 'Your basket is below this bakery’s minimum.',
  'error.paymentFailed': 'Your payment did not go through. Please try another card.',
  'error.servicePaused': 'Ordering is paused right now.',
  'error.notServable': 'We do not deliver to this address yet.',
  'error.cancellationClosed': 'Too late to cancel.',

  // -------------------------------------------------------------------------
  // Wire error vocabulary - PRD NFR-006.
  //
  // One entry per key in `ERROR_MESSAGE_KEYS` (`@croissants/contracts`), the
  // CLOSED set of `messageKey` values the platform can put on an error
  // response. It lives here, once, rather than in four app catalogues: a key
  // present in one portal's copy and absent from another's is precisely how an
  // operator read "Something went wrong." where the server had sent an
  // actionable provider diagnosis. `completeness.test.ts` walks the contracts
  // registry against this file in both locales, so registering a key without
  // copy fails the build.
  //
  // The keys are the wire's own snake_case; the camelCase `error.*` entries
  // above are the guest app's own screen copy and are a separate set.
  // -------------------------------------------------------------------------

  // Any request on any mount: the endpoint binder and the shared error mapper.
  'error.validation': 'Please correct the highlighted fields.',
  'error.idempotency_key_required': 'That action could not be sent safely. Please try again.',
  'error.idempotency_conflict':
    'That key was already used for a different request. Start the action again.',
  'error.support_case_stale': 'The order changed. Refresh and decide again.',
  'error.refund_over_charged':
    'That amount is more than the order was charged, after the other cases.',
  'error.support_case_invalid': 'This decision is not available for the current case.',
  'error.refund_unauthorized': 'An authenticated Internal operator must authorize this action.',
  'error.refund_order_not_final': 'A case can be opened only once the order is closed or canceled.',
  'error.support_case_not_found': 'The support case was not found.',
  'error.internal': 'Something went wrong on our side. Please try again.',
  'error.http': 'That request could not be completed.',
  'error.domain': 'That is not possible right now.',
  'error.cancellation_closed': 'Too late to cancel.',
  'error.acceptance_not_open': 'This order cannot be answered yet.',
  'error.rejection_reason_required': 'A reason is required to reject this order.',
  'error.guest_choice_required':
    'The guest chose this bakery, so it cannot be reassigned without asking them.',
  'error.immediate_only': 'That is only possible for immediate orders.',
  'error.stale_event': 'That update arrived out of order and was ignored.',
  'error.provider_limit': 'The delivery partner refuses those values.',
  'error.provider_update_ignored': 'The delivery partner did not apply that change.',
  'error.capability_unavailable': 'The delivery partner does not offer that.',
  'error.illegal_transition': 'That order has already moved on.',
  'error.qr_invalid': 'This code is not recognized.',
  'error.session_invalid': 'This session has expired. Scan the code again.',
  'error.order_not_found': 'That order does not exist.',
  'error.window_unavailable': 'That window is no longer available. Please pick another one.',
  'error.delivery_unavailable': 'Delivery is unavailable right now.',
  'error.window_passed': 'That time has passed.',
  'error.courier_quote_above_ceiling':
    'Delivery to this address costs too much right now. Please try another time.',
  // Names what to DO, because the review behind this message now says which
  // morning went: the guest reads it again and pays for the mornings left.
  'error.stay_morning_unavailable':
    'One of your mornings is no longer available. Please check your order before paying.',
  'error.ordering_closed': 'Ordering is closed for now.',
  'error.courier_en_route': 'The courier is already on the way.',
  'error.bag_photo_required': 'A photo of the bag is required.',
  'error.bag_photo_not_ready': 'This order is not ready for a bag photo.',
  'error.bag_photo_limit': 'This order already has 10 bag photos.',
  // Names the MORNING and not the address: this refusal is reached from an
  // address the platform serves, so "that address" read as "not your street".
  'error.no_bakery': 'No bakery can serve that morning.',
  'error.choose_shop': 'Please choose a bakery first.',
  // Mode B2: the bakery serving this apartment changed under the guest. Says
  // what happened and what it costs them - the basket - without naming a shop
  // they were never told about.
  'error.shop_changed': 'The bakery serving you has changed. Your basket was emptied.',
  'error.minimum_basket': 'Your basket is below this bakery’s minimum.',
  'error.payment_declined': 'Your payment did not go through. Please try another card.',
  'error.tip_unsupported': 'Tipping is not available for this delivery.',
  'error.tip_window_closed': 'The window for tipping has closed.',
  'error.rating_before_delivery': 'You can rate this order once it has been delivered.',
  'error.order_not_closed': 'Your invoices will be available once this order is complete.',
  'error.not_dispatchable': 'This order cannot be dispatched yet.',
  'error.order_ended': 'This order has ended; its delivery can no longer be changed.',
  'error.qr_cap': 'The code allowance for this account is used up.',
  'error.import_invalid': 'That file could not be imported. Check the columns and try again.',
  'error.unknown_entity': 'That record does not exist.',

  // Guest mount only.
  'error.code_expired': 'That code has expired. Request a new one.',
  'error.code_invalid': 'That code is not correct.',
  'error.tip_failed': 'Tip declined. Try another card.',
  'guest.not_servable': 'We do not deliver to this address yet.',
  'guest.service_paused': 'Ordering is paused right now.',

  // Partner mount, and the two address refusals the internal mount shares with it -
  // a shop typed in from outside Paris is the same mistake either side of the
  // portal, so it gets the same sentence.
  'error.address_outside_paris':
    'We only serve Paris for now, and that address is outside it. Check the postal code and the city.',
  'error.bad_base64': 'That scan could not be read.',
  'error.batch_not_found': 'That batch of codes does not exist.',
  'error.geocode_failed': 'That address did not resolve. Check it and try again.',
  'error.import_not_found': 'That import does not exist, or has expired.',
  'error.member_not_found': 'That team member does not exist.',
  'error.member_address_unknown':
    'This member has no known address, so their access cannot be changed here.',
  'error.member_already_in_team': 'That person is already a member.',
  'error.member_last_admin':
    "This is the organization's last administrator, so their access cannot be removed.",
  'error.member_address_rejected': 'The sign-in service refused that address.',
  'error.member_disabled': 'Your access to this organization has been disabled.',
  'error.member_self_disable': 'You cannot disable your own access.',
  'error.member_self_remove': 'You cannot remove yourself from the team.',
  'error.team_read_only': "This organization's team list is read-only.",
  'error.apartment_archived': 'An archived apartment cannot be restored.',
  'error.team_not_connected':
    'This organization is not connected to the sign-in service, so its team cannot be changed here.',
  'error.team_directory_unavailable': 'The sign-in service did not answer. Try again in a moment.',
  'error.payout_not_found': 'That payout does not exist.',
  'error.payout_held': "This organization's payouts are held.",
  'error.payout_nothing_available': 'Nothing is available to pay out.',
  'error.payout_identity_incomplete': 'The legal identity its invoice needs is incomplete.',
  'error.payout_account_not_verified': 'No verified payout account.',
  'error.payout_contact_missing': 'No payout contact on file.',
  'error.payout_bank_details_needed': 'New bank details are needed first.',
  'error.payout_account_restricted': 'Stripe restricted this account. The payout waits.',
  'error.payout_account_changed': 'The payout account changed. Preview again.',
  'error.payout_preview_stale': 'Another payout is on its way. Preview again.',
  'error.document_not_exportable': 'This document is not waiting for Pennylane.',
  'error.document_not_reissuable': 'This document cannot be canceled and reissued.',
  'error.invoice_identity_incomplete': "The bakery's legal identity is incomplete.",
  'error.invoice_not_created': 'Create the invoice first.',
  'error.delivery_statement_open': 'This cycle is still open.',
  'error.delivery_statement_matched': 'This statement is already matched to another invoice.',
  'error.delivery_statement_changed': 'Another invoice was just entered. Reload and try again.',
  'error.properties_not_found': 'Some of those apartments do not exist.',
  'error.property_not_found': 'That apartment does not exist.',
  'error.qr_cap_reached': 'The code allowance for this account is used up. Contact us to raise it.',
  'error.storefront_mode_not_allowed':
    'That menu type is not available for this organization. Ask Croissant in Bed to enable it.',
  'error.qr_not_generated': 'No codes have been generated for this apartment yet.',
  'error.no_qr_codes': 'No apartment has a QR code yet.',

  // Bakery mount only.
  'error.line_not_found': 'That line is not on this order.',
  'error.not_collectable': 'That order has no bag waiting for a courier yet.',
  'error.replacement_not_approved': 'That replacement has not been approved.',
  'error.quantity_above_ordered': 'That is more than the guest ordered.',
  'error.quantity_already_reduced': 'Those items have already been refunded.',
  'error.replacement_unavailable': 'That replacement is not available.',
  'error.delay_not_available':
    'More time can only be taken while the counter still has the bag, before a courier is assigned.',
  'error.cancel_not_available':
    'This order can no longer be canceled from the counter: the courier has the bag.',

  // Internal mount only.
  'error.alert_not_found': 'That alert does not exist.',
  'error.bakery_not_found': 'That bakery does not exist.',
  'error.bakery_org_mode_mismatch': 'That house does not sell in this mode.',
  'error.bakery_org_required': 'Choose the house this shop belongs to.',
  'error.has_pending_orders': 'This still has orders in progress. Delete it once they are done.',
  'error.delivery_not_rebookable': 'This delivery cannot be rebooked.',
  'error.courier_assigned': 'A courier has already taken this job: nothing was changed.',
  'error.provider_cancel_refused':
    'The delivery provider refused to cancel the job: nothing was rebooked.',
  // The three payout-onboarding refusals (OPS, PAY-032). They reach an operator
  // on the bakery organization detail screen.
  'error.house_not_trading': 'Reinstate this house before sending an onboarding link.',
  'error.legal_identity_incomplete': 'Fill in the legal identity before sending a link.',
  'error.payout_provider_unavailable': 'Stripe did not create the account. Try again.',
  'error.kill_city_required': 'A city is required for a city-wide kill switch.',
  'error.order_line_unknown': 'That line is not on this order.',
  'error.org_not_found': 'That organization does not exist.',
  'error.org_code_taken': 'Another organization has that code.',
  'error.org_code_reserved': 'That code belongs to a delivery provider.',
  'error.org_code_frozen': 'This code is already in use and can no longer change.',
  'error.partner_not_found': 'That partner does not exist.',
  'error.plan_unavailable': 'That confirmation is no longer good. Preview it again.',
  'error.rate_amount_required': 'An amount is required for that rate.',
  'error.rate_kind': 'That rate kind is not valid here.',
  'error.rate_scope_required': 'A commission names the partner who earns it.',
  // The liability review and the provider claim (PAY-013, PAY-030).
  'error.no_open_review': 'There is no open liability review on this order.',
  'error.decision_amount_required': 'A partial refund needs an amount.',
  'error.decision_amount_too_large': 'That is more than this refund review holds.',
  'error.review_not_open_yet':
    'The order has not ended yet. A refund decision waits until the bread has stopped moving.',
  'error.claim_action_unavailable': 'The claim cannot move that way from where it is.',
  'error.decision_unavailable': 'That is not one of the answers this refund review offers.',
  'error.partial_claim': 'A claim against the courier returns the whole of what the review holds.',
  'error.target_suspended': 'That shop is suspended and takes no orders.',
  'error.target_mode_incompatible': 'That house sells in the other storefront mode.',
  'error.target_other_house':
    'The guest chose this house. An order can only move to another of its shops.',
  'error.target_unpriced': 'That house has agreed no price for something in this basket.',
  'error.target_closed': "That shop is closed on this order's day.",
  'error.refund_amount_unsupported': 'A partial amount is not supported for this refund.',
  'error.small_order_fee_incomplete':
    'A small-order fee needs both a basket threshold and an amount.',
  'error.catalogue_item_not_found': 'That menu item no longer exists.',
  'error.media_too_large':
    'That image is too large to store on this deployment. Use a smaller file.',
  'error.pool_not_writable': 'Only the platform shelf and a mode B house own range can be edited.',
  'error.legal_identity_platform_managed': 'To change these details, contact Croissant in Bed.',
  'error.tax_kind_locked': 'To change your tax status, contact Croissant in Bed.',
  'error.stripe_identity_locked':
    'Stripe has verified these details, so they can\u2019t be changed. To use new ones, create a new account.',
  'error.payout_details_incomplete':
    'Some details are still missing. Check the form and try again.',
  'error.organization_suspended': 'This organization is suspended. Contact us to reactivate it.',

  // The kinds a menu is divided into (CAT-016).
  'error.product_type_exists': 'A kind with that name already exists.',
  'error.product_type_not_found': 'That kind no longer exists.',
  'error.product_type_in_use': 'Move or retire the products in this kind first.',
  'error.product_type_built_in':
    'This kind is part of the product and cannot be renamed or removed.',
  'error.product_type_name_required': 'A kind needs an English name.',

  // The handheld release channel (OPS-029, docs/plans/handheld-fleet.html).
  // Internal-mount refusals, read by an operator on the Releases screen - and
  // the same sentences the internal catalogue carries, word for word, because
  // this one is the English fallback an `apiError` travels with when a surface
  // has no copy of its own.
  'error.handheld_release_exists': 'A build with that version code is already stored.',
  'error.handheld_release_not_found': 'That build no longer exists.',
  'error.handheld_version_not_above':
    'A build has to be newer than the one already published. Counters refuse anything older.',
  'error.handheld_version_below_minimum':
    'The forced minimum only ever goes up. Remove the force first to go back to an older build.',
  'error.handheld_not_latest':
    'Only the build the fleet is being offered right now can become the minimum. Publish this one first.',
  'error.handheld_release_unsigned':
    'This build has no signature yet, and every counter would discard it. Sign the manifest first.',
  'error.handheld_release_halted': 'That build was halted and cannot be published.',
  'error.handheld_release_not_published': 'Only a published build can become the minimum.',
  'error.handheld_manifest_mismatch':
    'That manifest does not describe this build. Paste the one the platform showed for it.',
  'error.handheld_manifest_channel': 'That manifest was signed for the other environment.',
  'error.handheld_signature_invalid':
    'That signature was not made by the key it names. Check which key signed it.',
  'error.handheld_rollout_incomplete':
    'This build has not reached the whole fleet yet. Widen it to 100% before forcing it.',
  'error.handheld_confirmation_mismatch': 'Type the version number to confirm.',
  'error.handheld_location_unknown': 'One of the shops chosen no longer exists.',
  'error.handheld_threshold_pair':
    'A clearing threshold must leave a gap: the clock-skew clear goes below its raise, and the storage clear above its own.',
  'error.handheld_device_not_found': 'That counter device no longer exists.',
  'error.handheld_nothing_published': 'Nothing is published, so there is no rollout to widen.',
  'error.handheld_rollout_not_widening': 'A rollout only widens; choose a larger share.',
  'error.handheld_release_not_halted': 'That build was not halted.',
  'error.handheld_diagnostic_not_found': 'That diagnostic document no longer exists.',

  // Reading a scenario run's evidence out of the RUNS bucket (OPS-038). The
  // first is a deployment fault rather than an empty screen: an operator told
  // "no runs yet" could not tell that apart from a bucket nothing published to.
  'error.run_evidence_unavailable': 'Run evidence storage is not configured on this instance.',
  'error.run_not_found': 'No run evidence was published under that id.',
  'error.run_shot_not_found': 'That screenshot is not in this run evidence.',
  'error.whatsapp_not_configured': 'WhatsApp is not set up on this instance yet.',
  'error.whatsapp_unreachable': 'WhatsApp did not answer. Try again in a minute.',

  // Composing a formule and pricing it per house (CAT-017, CAT-018). Read by an
  // operator in the internal portal, so each one says what to do about it.
  'error.not_a_formule': 'Only a formule is composed of other products.',
  'error.formule_in_formule': 'A formule cannot contain another formule.',
  'error.formule_answers': 'Choose an option for every part of this formule.',
  'error.formule_without_components':
    'A formule needs at least one item before it can go on the menu.',
  'error.product_code_taken': 'That product code already names another product.',
  'error.choice_not_in_pool': 'A choice must be a product already on the platform menu.',
  'error.choice_retired': 'A retired product cannot be offered as a choice.',
  'error.choice_in_published_formule':
    'A set on the menu still lists this product. Take it out of the set first.',
  'error.section_empty': 'A section needs at least one product.',
  'error.choice_needs_options': 'A section the guest chooses from needs at least two options.',
  'error.negative_commission': 'The bakery price cannot be higher than the guest price.',
  'error.guest_price_below_bakery_price':
    'That guest price is below what a bakery keeps for this product. Lower its price first.',
  'error.price_follows_parts': "This set's price follows its products, so it cannot be changed.",
  'error.set_parts_unpriced': 'Price every product of this set for this bakery first.',
  'error.bakery_price_required': 'Enter the bakery price.',

  // Public site forms (WEB-025, WEB-026). The static pages cannot import this
  // catalogue - no build step, no module system - so site.js repeats these two
  // strings; they are registered and translated here because they are the
  // platform's answer, and any portal that ever surfaces them must not have to
  // invent one.
  'error.bot_check_failed': 'The bot check did not pass. Please try again.',
  'error.bot_check_unavailable': 'The bot check is unavailable. Please try again shortly.',

  // Webhook mount: read by a provider, never by a person. Registered for wire
  // validation; no catalogue is held to them.
} as const;

export type EnCatalogue = typeof en;
